<template>
  <div>
    <h1 class="page-title mb-5">{{ $t('CandidateJoinEntities.title') }}</h1>

    <form @submit.prevent="onValidate" class="container">
      <div class="col-lg-10 form-position-center">

        <div class="form-group form-group-default required">
          <label>{{ $t("models.user.entity_code") }}</label>
          <div class="controls">
            <input class="form-control"
                  id="entityCode"
                  v-model="entityCode"
                  name="entityCode"
                  type="entityCode"
                  v-validate="`required|max:${entityCodeSizeMax}|min:${entityCodeSizeMin}`"
                  required=""
                  maxlength="5"
                  aria-required="true">
          </div>

          <transition name="slide-fade">
            <label v-show="errors.has('entityCode')" id="entityCode-error" class="error" for="entityCode">
              {{errors.first('entityCode') }}
            </label>
          </transition>
        </div>

        <div v-if="entityCodeValidity">
          <div class="form-group form-group-default required">
            <label>{{ $t("UserForm.establishment") }}</label>
            <input :value="entity.name" name="entity" readonly type="text" class="form-control text-black">
          </div>

          <candidate-entity-form :userEntity="mainUserEntity"
                                 @change="onChangePrimaryForm"
                                 :key="`main_form_${keyIndex}`" />
        </div>
      </div>

      <div v-if="showTable" class="justify-content-center col-12 mt-4">
        <h4 class="table-title">
          <span>{{ $t('CandidateJoinEntities.table_title') }}</span>
        </h4>

        <candidate-entities-table :userEntities="selectedSiblingUserEntities"
                                  :withActions="false" />
      </div>

      <div v-if="entityCodeValidity">
        <div :class="modalButtonClass">
          <button v-if="!isSingleEntity"
                  type="button"
                  class="btn btn-modal btn-cons m-t-10 btn-edit__user-entities"
                  @click="onShowModal">
            {{ modalButtonText }}
          </button>
        </div>

        <div class="d-flex align-items-center justify-content-center mt-5">
          <router-link :to="{ name: 'CandidateEntities' }"
                        class="btn btn-default btn-cons">
            {{ $t("buttons.cancel") }}
          </router-link>
          <button type="submit"
                  class="btn btn-primary btn-cons">
            {{ $t("buttons.validate") }}
          </button>
        </div>
      </div>
    </form>

    <template>
      <candidate-select-entities-modal modalId="MultpleEntitiesModal"
                                     :userEntities="siblingsUserEntities"
                                     :allManageableOptions="siblingsManageableOptions"
                                     :key="`modal_${keyIndex}`"
                                     @validate="onValidateModal" />
    </template>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import CandidateEntityForm from './CandidateEntityForm'
  import CandidateSelectEntitiesModal from './CandidateSelectEntitiesModal'
  import CandidateEntitiesTable from './CandidateEntitiesTable'
  import { getEntitySiblingsByCode, getEntityResourceByCode } from '@/backend/entities'

  export default {
    name: 'candidate-join-entities',
    components: {
      CandidateEntityForm,
      CandidateSelectEntitiesModal,
      CandidateEntitiesTable
    },
    data: function() {
      return({
        keyIndex: 0,
        entityCode: '',
        entityCodeSizeMin: 4,
        entityCodeSizeMax: 5,
        mainUserEntity: null,
        siblingsUserEntities: [],
        siblingsManageableOptions: {}
      });
    },
    computed: {
      ...mapGetters({
        currentUser: 'auth/currentUser',
        entityCodeValidity: 'users/entityCodeValidity',
        entity: 'users/entity',
        isSingleEntity: 'users/isSingleEntity'
      }),
      userId() {
        return this.currentUser.id
      },
      selectedSiblingUserEntities() {
        return this.siblingsUserEntities.filter(userEntity => {
          return !userEntity.alreadyRegistered && userEntity.selected
        });
      },
      selectedUserEntities() {
        return [this.mainUserEntity].concat(this.selectedSiblingUserEntities);
      },
      showTable() {
        return this.entityCodeValidity && this.selectedSiblingUserEntities.length > 0;
      },
      modalButtonClass() {
        return this.showTable ? "col-lg-12 form-position-center" : "col-lg-6 form-position-center"
      },
      modalButtonText() {
        return this.showTable ? this.$t("CandidateJoinEntities.modal_button_modify")
                              : this.$t("CandidateJoinEntities.modal_button_add");
      }
    },
    methods: {
      ...mapActions({
        updateManageableOptions: 'users/updateManageableOptions',
        updateEntityCodeValidity: 'users/updateEntityCodeValidity',
        initManageableOptions: 'users/initManageableOptions',
        createUserEntities: 'users/createUserEntities'
      }),
      createUserEntity(entity, resourceType) {
        return {
          entity: entity,
          resource_type: resourceType || 'extern',
          affiliated_service: null,
          services: [],
          occupations: []
        }
      },
      initMainUserEntity() {
        const userEntity = this.createUserEntity(this.entity, 'intern');
        this.mainUserEntity = userEntity;
      },
      async initSiblingsUserEntities() {
        let siblingsEntities;
        await getEntitySiblingsByCode(this.entityCode).then(response => {
          siblingsEntities = response.data;
        });

        siblingsEntities.forEach(entity => {
          const alreadyRegistered = this.alreadyRegistered(entity);
          this.siblingsUserEntities.push({ ...this.createUserEntity(entity),
                                            selected: alreadyRegistered,
                                            alreadyRegistered: alreadyRegistered });
          this.initSiblingsManageableOption(entity);
        });
      },
      async initSiblingsManageableOption(entity) {
        await getEntityResourceByCode(entity.entity_code).then(response => {
          this.siblingsManageableOptions[entity.id] = response.data;
        });
      },
      alreadyRegistered(entity) {
        return this.currentUser.entities.some(element => element.id == entity.id);
      },
      onChangePrimaryForm(value) {
        this.mainUserEntity[value.field] = value.data;
      },
      onValidateModal(value) {
        this.siblingsUserEntities = value;
      },
      onShowModal() {
        $('#MultpleEntitiesModal').modal('show');
      },
      onValidate() {
        this.$validator.validate().then((isValid) => {
          if(isValid) {
            this.createUserEntities(this.selectedUserEntities)
            this.$router.push({ name: 'CandidateEntities' });
          }
        })
      },
      resetEntitiesData() {
        this.userEntities = [];
        this.siblingsUserEntities = [];
        this.siblingsManageableOptions = [];
        this.initMainUserEntity();
        this.initSiblingsUserEntities();
      }
    },
    watch: {
      entityCode() {
        if (this.entityCode.length >= this.entityCodeSizeMin &&
            this.entityCode.length <= this.entityCodeSizeMax
        ) {
          this.updateManageableOptions({
            entityCode: this.entityCode,
            email: this.currentUser.email
          });
        } else {
          this.updateEntityCodeValidity(false);
        }
      },
      entity() {
        this.resetEntitiesData();
        // force reload mainForm and modal by changing the key value
        this.keyIndex++;
      }
    },
    created() {
      this.updateEntityCodeValidity(false);
    }
  }
</script>

<style lang="scss">
.btn-edit__user-entities {
  background: #e51349;
  color: white;
  &:hover {
    background: #e51349;
    color: white;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/entities/CandidateJoinEntities.vue